<template>
  <v-list
    class="d-menu py-0"
    max-width="267"
  >
    <!-- (mobile) Show Logo -->
    <v-list-item v-if="mobileVariant">
      <v-list-item-content class="text-center">
        <a
          href="#"
          class="d-block"
          @click="reload"
        >
          <img
            contain
            max-height="31"
            max-width="150"
            :src="$vuetify.theme.dark ? '/assets/logo-white.svg' : '/assets/logo-blue.svg'"
          >
        </a>
      </v-list-item-content>
    </v-list-item>

    <!-- Account Switcher Menu -->
    <account-switcher
      :mini-variant="miniVariant"
      :collapsable="collapsable"
    />
    <!-- /// -->

    <v-divider class="mb-2" />

    <!-- (mobile) Search and Unread Reservations -->
    <template v-if="mobileVariant">
      <g-menu-item
        :title="$t('title.search')"
        icon="mdi-magnify"
        to="/search"
        link
      />
      <g-menu-item
        :title="$t('title.unread_reservations')"
        icon="mdi-email-open-outline"
        to="/unread-reservations"
        :badge="unreadReservationsBadge"
        link
      />
      <v-divider class="my-2" />
    </template>
    <!-- /// -->

    <!-- Main Views -->
    <g-menu-item
      v-if="hasUserAccounts"
      :title="$t('title.accounts')"
      icon="mdi-chart-box-multiple-outline"
      filled-icon="mdi-chart-box-multiple"
      to="/accounts"
      :tooltip="miniVariant"
      link
    />
    <g-menu-item
      :title="$t('title.dashboard')"
      icon="mdi-view-dashboard-outline"
      filled-icon="mdi-view-dashboard"
      to="/dashboard"
      :tooltip="miniVariant"
    />
    <v-divider
      v-if="hasUserAccounts"
      class="my-2"
    />
    <g-menu-item
      v-for="(item, i) in reservationMenuItems()"
      :key="`menu_reservationItems_${i}`"
      :title="item.text"
      :icon="item.icon"
      :filled-icon="item.filledIcon"
      :to="item.link"
      :label-badge="item.labelBadge"
      :disabled="!item.link"
      :tooltip="miniVariant"
      link
    />
    <v-divider class="my-2" />

    <g-menu-item
      :title="$t('title.contacts')"
      icon="mdi-contacts-outline"
      filled-icon="mdi-contacts"
      to="/contacts"
      :tooltip="miniVariant"
      link
    />
    <v-divider class="my-2" />
    <main-menu-group
      :title="$t('title.newsletters')"
      icon="mdi-email-multiple-outline"
      filled-icon="mdi-email-multiple"
      :menu-items="newsletterMenuItems()"
      :mini-variant="miniVariant"
      @menu-expand="$emit('menu-expand')"
    />
    <v-divider class="my-2" />

    <main-menu-group
      :title="$t('title.reports')"
      icon="mdi-chart-timeline-variant-shimmer"
      :menu-items="reportsItems"
      :mini-variant="miniVariant"
      @menu-expand="$emit('menu-expand')"
    />
    <v-divider class="my-2" />

    <!-- Feedback -->
    <g-menu-item
      :title="$t('title.feedback')"
      icon="mdi-message-star-outline"
      filled-icon="mdi-message-star"
      to="/feedback"
      :tooltip="miniVariant"
      link
    />
    <v-divider class="my-2" />

    <!-- Settings -->
    <v-tooltip
      right
      :disabled="!miniVariant"
      open-delay="270"
    >
      <template #activator="{ on, attrs }">
        <v-list-group
          v-model="settingsGroup"
          v-bind="attrs"
          no-action
          class="d-menu"
          v-on="on"
          @click="settingsClicked"
        >
          <template #activator>
            <v-list-item-action @click="settingsClicked">
              <v-icon>{{ settingsGroup ? 'mdi-cog' : 'mdi-cog-outline' }}</v-icon>
            </v-list-item-action>
            <v-list-item-content @click="settingsClicked">
              <v-list-item-title>{{ $t('menu.settings') }}</v-list-item-title>
            </v-list-item-content>
          </template>

          <template v-for="(item, i) in settingItems">
            <g-menu-item
              v-if="item.subitems === undefined"
              :key="`menu_settings_${i}`"
              :title="item.text"
              :to="item.link"
              :disabled="!item.link"
              :label-badge="item.labelBadge"
              link
            />
            <v-list-group
              v-else
              :key="`menu_settings_b_${i}`"
              no-action
              sub-group
              link
            >
              <template #activator>
                <v-list-item-content v-if="!item.link">
                  <v-list-item-title>{{ item.text }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-content
                  v-else
                  @click="$router.push('/settings/apps')"
                >
                  <v-list-item-title>{{ item.text }}</v-list-item-title>
                </v-list-item-content>
              </template>
              <g-menu-item
                v-for="(subitem, j) in item.subitems"
                :key="`menu_settings_${i}_${j}`"
                :to="subitem.link"
                :title="subitem.text"
                :disabled="!subitem.link || subitem.disabled"
                :label-badge="subitem.labelBadge"
                link
              />
            </v-list-group>
          </template>
        </v-list-group>
      </template>
      <span>{{ $t('menu.settings') }}</span>
    </v-tooltip>

    <!-- Apps -->
    <v-tooltip
      v-if="appsMenuItems.length > 0"
      right
      :disabled="!miniVariant"
      open-delay="270"
    >
      <template #activator="{ on, attrs }">
        <v-list-group
          v-model="appsGroup"
          v-bind="attrs"
          no-action
          class="d-menu"
          v-on="on"
          @click="appsClicked"
        >
          <template #activator>
            <v-list-item-action @click="appsClicked">
              <v-icon>mdi-apps</v-icon>
            </v-list-item-action>
            <v-list-item-content @click="appsClicked">
              <v-list-item-title>{{ $t('title.apps') }}</v-list-item-title>
            </v-list-item-content>
          </template>

          <template v-for="(item, i) in appsMenuItems">
            <g-menu-item
              v-if="item.subitems === undefined"
              :key="`menu_apps_${i}`"
              :title="item.text"
              :to="item.link"
              :disabled="!item.link"
              :label-badge="item.labelBadge"
              link
            />
            <v-list-group
              v-else
              :key="`menu_apps_sub_${i}`"
              no-action
              sub-group
              link
            >
              <template #activator>
                <v-list-item-content v-if="!item.link">
                  <v-list-item-title>{{ item.text }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-content
                  v-else
                  @click="$router.push('/apps/')"
                >
                  <v-list-item-title>{{ item.text }}</v-list-item-title>
                </v-list-item-content>
              </template>
              <g-menu-item
                v-for="(subitem, j) in item.subitems"
                :key="`menu_apps_${i}_${j}`"
                :to="subitem.link"
                :title="subitem.text"
                :disabled="!subitem.link || subitem.disabled"
                :label-badge="subitem.labelBadge"
                link
              />
            </v-list-group>
            <v-divider
              v-if="i===0 && appsMenuItems.length > 1"
              :key="`menu_apps_${i}_div`"
              class="my-2"
            />
          </template>
        </v-list-group>
      </template>
      <span>{{ $t('title.apps') }}</span>
    </v-tooltip>

    <template v-if="inlineHelpAllowed">
      <v-divider class="my-2" />
      <g-menu-item
        :title="$t('button.help')"
        icon="mdi-help-circle-outline"
        href="https://docs.guestplan.com/"
        target="_blank"
        link
      />
      <g-menu-item
        :title="$t('title.whats_new')"
        icon="mdi-bullhorn-variant-outline"
        @click="$emit('show-release-notes')"
      />
    </template>

    <template v-if="pwaDebug">
      <v-divider class="my-2" />
      <g-menu-item
        title="PWA Info"
        to="/pwa-info"
        icon="mdi-cellphone-cog"
        link
      />
    </template>
    <template v-if="isInstallable">
      <v-divider class="my-2" />
      <g-menu-item
        :title="$t('menu.install_app')"
        icon="mdi-cellphone-arrow-down"
        link
        @click="installPrompt"
      />
    </template>
    <v-divider
      class="my-2"
      style="padding-bottom: 112px"
    />

    <!-- Sticky Footer -->
    <v-sheet
      class="main-menu-footer pl-0 pr-0 pb-0"
      bottom
    >
      <v-divider class="pa-0" />

      <!-- (desktop, collapsed) decollapse -->
      <g-menu-item
        v-if="collapsable && miniVariant"
        tooltip
        class="pb-1"
        :title="$t('label.expand_menu')"
        icon="mdi-chevron-double-right"
        @click="$emit('menu-expand')"
      />

      <div
        v-if="!miniVariant"
        class="d-flex justify-space-between main-menu-iconbar"
      >
        <tooltip-icon-button
          button-class="pl-0 ml-1 mr-4"
          :tooltip-title="$t('button.logout')"
          @click="$emit('logout')"
        >
          <v-icon>
            mdi-logout
          </v-icon>
        </tooltip-icon-button>

        <tooltip-icon-button
          button-class="mx-4"
          :tooltip-title="$vuetify.theme.dark ? $t('menu.light_mode') : $t('menu.night_mode')"
          @click="$emit('dark-mode', true)"
        >
          <v-icon>
            {{ $vuetify.theme.dark ? 'mdi-weather-sunny' : 'mdi-weather-night' }}
          </v-icon>
        </tooltip-icon-button>

        <!-- Language Picker -->
        <v-menu offset-y>
          <template #activator="{ on, attrs }">
            <v-btn
              text
              class="pl-1 mx-0 px-0"
              :color="$vuetify.theme.dark ? 'primary' : '#757575'"
              v-bind="attrs"
              v-on="on"
            >
              {{ $i18n.locale.split('-')[0].toUpperCase() }}
              <v-icon
                small
                class="px-0 mx-0"
              >
                mdi-chevron-down
              </v-icon>
            </v-btn>
          </template>

          <v-list class="py-0">
            <v-subheader
              class="py-0"
              style="height: 28px"
            >
              {{ $tc('label.language').toUpperCase() }}
            </v-subheader>
            <v-divider />
            <v-list-item
              v-for="l in sortedSupportedLanguages"
              :key="l.value"
              class="pl-2"
              dense
              @click="changeLanguage(l.value)"
            >
              <v-list-item-icon class="mr-0">
                <v-icon
                  v-if="$i18n.locale.split('-')[0] === l.value"
                  small
                  color="primary"
                >
                  mdi-check
                </v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ l.text }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <!--  -->

        <v-btn
          v-if="!mobileVariant"
          icon
          class="ml-4 mr-1"
          @mousedown.stop
          @touchstart.native.stop
          @click.stop="collapseMenu"
        >
          <v-icon>mdi-chevron-double-left</v-icon>
        </v-btn>
      </div>
    </v-sheet>
  </v-list>
</template>

<script setup lang="ts">
import AccountSwitcher from '@/components/views/main/AccountSwitcher.vue';
import MainMenuGroup from '@/components/views/main/MainMenuGroup.vue';
import GMenuItem from '@/components/util/MenuItem.vue';
import i18n, { supportedLanguages } from '@/plugins/i18n';

import { computed, ref } from 'vue';
import {
  MenuItem, reservationMenuItems, settingsMenuItems, reportsMenuItems, newsletterMenuItems,
} from '@/util/menu';

import { performAction } from '@/services/vue-utils';
import GstplnApp from '@/components/apps/app';
import setLanguage from '@/plugins/i18n-locale';
import { tryCloseEditReservation } from '@/services/reservation-edit-utils';
import { useToast, useVuetify } from '@/components/helpers/vue2-helpers';
import store from '@/store/store';
import usePwaInstall from '@/composables/pwa-install';
import useActivityIndicator from '@/composables/activity-indicator';
import { hasMultipleAccounts } from '@/composables/account-switcher';

const inlineHelpAllowed = import.meta.env.VITE_INLINE_DOCS === 'true';
const pwaDebug = !import.meta.env.PROD;

const vuetify = useVuetify();
const emit = defineEmits(['menu-collapse', 'menu-expand', 'logout', 'dark-mode', 'show-release-notes']);

const props = defineProps({
  miniVariant: { type: Boolean, required: false, default: false },
  collapsable: { type: Boolean, required: false, default: false },
});

const { showActivity } = useActivityIndicator();

const mobileVariant = computed(() => vuetify.breakpoint.smAndDown);

const unreadReservationsBadge = computed(() => {
  const unread = store.getters.unreadReservationsCount;
  return unread > 0 ? `${unread}` : null;
});

const hasUserAccounts = computed(() => hasMultipleAccounts());

const settingItems = computed(() => {
  const billingPageAllowed = store.getters.isBillingPageAllowed;
  return settingsMenuItems({ billingPageAllowed });
});

const reportsItems = computed(() => reportsMenuItems());

const accountLanguage = computed(() => (store.state.settings.account.locale ?? i18n.locale ?? '').split('-')[0]);

const sortedSupportedLanguages = computed(() => supportedLanguages.toSorted((a, b) => {
  if (a.value === accountLanguage.value) return -1;
  if (b.value === accountLanguage.value) return 1;
  return a.text.localeCompare(b.text);
}));

const appsMenuItems = computed(() => {
  const gstplnApps = store.getters.gstplnApps as GstplnApp[];
  // no apps -> no apps menu
  if (gstplnApps.length === 0) return [];

  const menuApps = gstplnApps.filter((ai) => ai.installed && ai.hasMenuItem);
  const appMenuItems: MenuItem[] = menuApps.map((ai) => {
    const subitems = (ai.routeConfig?.length ?? 0) > 1
      ? ai.routeConfig?.filter((rc) => rc.title !== undefined)
        .map((rc) => ({ text: rc.title, link: `${ai.url}/${rc.route}` } as MenuItem))
      : undefined;
    return {
      text: ai.title(), link: subitems ? undefined : ai.url, icon: ai.icon, subitems,
    };
  });
  return [
    { text: i18n.t('title.manage_apps'), link: '/apps/manage' }, ...appMenuItems,
  ];
});

// Menu Collapse/Decollapse
const settingsGroup = ref(false);
const appsGroup = ref(false);

const collapseMenu = () => {
  appsGroup.value = false;
  settingsGroup.value = false;
  emit('menu-collapse');
};

const settingsClicked = () => { emit('menu-expand'); };
const appsClicked = () => { emit('menu-expand'); };

//

const reload = async () => {
  await performAction(
    null,
    i18n.tc('error.load_data'),
    async () => store.dispatch('update'),
  );
};

const changeLanguage = async (lang: string) => {
  if (!await tryCloseEditReservation()) { return; }

  showActivity(true);
  const cc = store.state.settings.account.countryCode || '';
  setLanguage(lang, cc);
  // eslint-disable-next-line no-restricted-globals
  location.reload();
};

// PWA
const { isInstallable, installPrompt } = usePwaInstall();

</script>

<style scoped>

  .main-menu-footer {
    left: 0px;
    right: 1px;
    bottom: 0px;
    position: absolute;
    z-index: 10;
  }

  .subgroup-with-link ::v-deep(.v-list-item__icon.v-list-group__header__prepend-icon) {
    margin-left: 0px !important;
  }

</style>
